// Roles, positions, departments shared between Team, Hiring Request, and edit dialogs.

export const POSITIONS = [
  "Super Admin",
  "Admin",
  "Department Admin",
  "Content Manager",
  "Content Team Lead",
  "Publishing Team Lead",
  "Writer",
  "Publisher",
  "Editor",
  "HR",
  "SEO Analyst",
  "SEO Executive",
  "Graphic Designer",
  "Project Designer",
  "Sales Executive",
  "Sales Publisher",
];

// Every position maps to a coarse RBAC "role" that the backend uses.
export const POSITION_TO_ROLE = {
  "Super Admin": "super_admin",
  "Admin": "admin",
  "Department Admin": "department_admin",
  "Content Manager": "content_manager",
  "Content Team Lead": "team_lead",
  "Publishing Team Lead": "team_lead",
};
export const positionRole = (p) => POSITION_TO_ROLE[p] || "associate";

// Positions where "intern vs associate" and joining date apply.
// Excluded: TL, Admin, CM (top-tier roles).
export const positionAllowsMemberType = (p) => !(p in POSITION_TO_ROLE);

export const DEPARTMENTS = [
  { value: "content",   label: "Content" },
  { value: "seo",       label: "SEO" },
  { value: "marketing", label: "Marketing" },
  { value: "hr",        label: "HR" },
];
export const DEPT_LABEL = Object.fromEntries(DEPARTMENTS.map((d) => [d.value, d.label]));

// A user may belong to multiple departments. Prefer the `departments` array
// but fall back to the legacy singular `department` string for older records.
export function userDepartments(user) {
  if (!user) return [];
  if (Array.isArray(user.departments) && user.departments.length > 0) return user.departments;
  if (user.department) return [user.department];
  return [];
}
export function isInDepartment(user, dept) {
  return userDepartments(user).map((d) => (d || "").toLowerCase()).includes((dept || "").toLowerCase());
}

// Human-readable label for the coarse `role` (used when `position` is empty).
export const ROLE_TO_POSITION_LABEL = {
  super_admin: "Super Admin",
  admin: "Admin",
  department_admin: "Department Admin",
  content_manager: "Content Manager",
  team_lead: "Team Lead",
  associate: "Associate",
};

// Iter 36.6 — Multi-role UI. Return every (department, position) pair the
// user holds, normalized to the shape `{department, position, role, ...}`.
// Falls back to the legacy top-level (department, position) pair when the
// modern `positions[]` array is empty (older accounts pre-migration).
export function allDisplayPositions(user) {
  if (!user) return [];
  const src = Array.isArray(user.positions) && user.positions.length > 0
    ? user.positions
    : [{
        role: user.role,
        department: user.department,
        position: user.position,
        manager_id: user.manager_id,
        team_lead_id: user.team_lead_id,
        team_name: user.team_name,
        tl_kind: user.tl_kind,
      }];
  const seen = new Set();
  const out = [];
  for (const p of src) {
    if (!p) continue;
    // For a team_lead, sharpen "Team Lead" into "Content Team Lead" /
    // "Publishing Team Lead" when tl_kind is known — that's what admins
    // actually want to see.
    let posLabel = p.position || "";
    if (!posLabel) {
      if (p.role === "team_lead") {
        posLabel = p.tl_kind === "publishing" ? "Publishing Team Lead" : "Content Team Lead";
      } else {
        posLabel = ROLE_TO_POSITION_LABEL[p.role] || (p.role || "").replace("_", " ") || "—";
      }
    }
    const key = `${p.department || ""}::${posLabel}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      role: p.role || "",
      department: p.department || "",
      position: posLabel,
      manager_id: p.manager_id || "",
      team_lead_id: p.team_lead_id || "",
      team_name: p.team_name || "",
      tl_kind: p.tl_kind || "",
    });
  }
  return out;
}

// Every team_name the user is a member of, either directly (TL role with
// team_name) or transitively (associate whose team_lead_id points at a TL
// with a team_name). Iter 36.6 pulls from BOTH the top-level fields and
// the `positions[]` array so multi-role users show all their teams.
export function memberTeamNames(user, allUsers) {
  if (!user) return [];
  const names = new Set();
  const positions = allDisplayPositions(user);
  const byId = new Map((allUsers || []).map((u) => [u.id, u]));
  for (const p of positions) {
    if (p.team_name) { names.add(p.team_name); continue; }
    if (p.team_lead_id) {
      const tl = byId.get(p.team_lead_id);
      if (tl?.team_name) names.add(tl.team_name);
      // Second hop — TL's team_name via THEIR positions[]
      const tlPositions = allDisplayPositions(tl);
      for (const tp of tlPositions) {
        if (tp.team_name) names.add(tp.team_name);
      }
    }
  }
  return Array.from(names);
}

export const MEMBER_TYPES = [
  { value: "intern",    label: "Intern" },
  { value: "associate", label: "Associate" },
];

export const POSITION_CLS = {
  "Super Admin":          "bg-amber-100 text-amber-800 border-amber-300",
  "Admin":                "bg-purple-100 text-purple-700 border-purple-200",
  "Department Admin":     "bg-violet-100 text-violet-700 border-violet-200",
  "Content Manager":      "bg-purple-100 text-purple-700 border-purple-200",
  "Content Team Lead":    "bg-blue-100 text-blue-700 border-blue-200",
  "Publishing Team Lead": "bg-cyan-100 text-cyan-700 border-cyan-200",
  "Writer":               "bg-emerald-100 text-emerald-700 border-emerald-200",
  "Publisher":            "bg-amber-100 text-amber-700 border-amber-200",
  "Editor":               "bg-pink-100 text-pink-700 border-pink-200",
  "HR":                   "bg-rose-100 text-rose-700 border-rose-200",
  "SEO Analyst":          "bg-teal-100 text-teal-700 border-teal-200",
  "SEO Executive":        "bg-teal-100 text-teal-700 border-teal-200",
  "Graphic Designer":     "bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200",
  "Project Designer":     "bg-violet-100 text-violet-700 border-violet-200",
  "Sales Executive":      "bg-yellow-100 text-yellow-700 border-yellow-200",
  "Sales Publisher":      "bg-orange-100 text-orange-700 border-orange-200",
};

// Days remaining before internship ends. Returns null if not an intern or no date.
export function daysUntilInternshipEnd(user) {
  if (user?.member_type !== "intern") return null;
  const end = user?.internship_end_date;
  if (!end) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const endDt = new Date(end + "T00:00:00");
  const diff = Math.round((endDt - today) / (24 * 3600 * 1000));
  return diff;
}
